import { ColorString, TimeDistanceMode } from '@fe/core';
import { DEFAULT_ROUTE_FLOWN_COLOR } from '@fe/flight-layer';
import React, { useCallback } from 'react';
import { useEvent } from '../../../../contexts/event-bus';
import WorkspaceItemContext from '../../../../contexts/workspace/workspace-item-context';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { useSafeEffect } from '../../../../hooks/use-safe-effect';
import { useFocusedWorkspaceSelector, useWorkspace, useWorkspaceView } from '../../hooks/use-view';

export function FlightRoutes() {
    const context = React.useContext(WorkspaceItemContext);
    const routeAi = useWorkspace((x) => x.routeAi);
    const flightPlanSettings = useWorkspace((x) => x.flights.settings.flightPlan);
    const drawDirectRouteWhenNoRouteAvailable = useAppSelector((x) => x.settings.drawDirectRouteWhenNoRouteAvailable);
    const flownRouteSettings = useWorkspace((x) => x.flights.settings.routeFlown);
    const showImpacts = useWorkspace((x) => x.flights.settings.impacts);
    const showProjectedPoints = useWorkspaceView((x) => x.plane.settings.showProjectedPoints);
    const timeDistanceMode = useWorkspaceView((x) => x.plane.settings.udViewTimeDistanceMode);
    const shouldColorizeRouteFlown = useAppSelector((x) => x.settings.colorizeRouteFlown);
    const shouldSmoothenRouteFlown = useAppSelector((x) => x.settings.smoothenRouteFlown);
    const routeFlownSettings = useWorkspaceView((x) => x.plane.settings.routeFlown);
    // TODO: There is no view settings for route flown color. Decide: stay/remove.
    const routeFlowColor = useFocusedWorkspaceSelector((x) => x.view.plane.settings.routeFlownColor);
    const etopsBorderFillColor = useAppSelector((x) => x.settings.etopsBorderFillColor);
    const etopsBorderOutlineColor = useAppSelector((x) => x.settings.etopsBorderOutlineColor);
    const etopsRuleFillColor = useAppSelector((x) => x.settings.etopsRuleFillColor);
    const etopsRuleOutlineColor = useAppSelector((x) => x.settings.etopsRuleOutlineColor);
    const etopsPointColor = useAppSelector((x) => x.settings.etopsPointColor);
    const etopsAirportColor = useAppSelector((x) => x.settings.etopsAirportColor);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.addRouteAi(routeAi);
    }, [context, routeAi]);

    useEvent(
        'flight-routes-updated',
        useCallback(
            (data) => {
                // Skip flights not present in the given workspace
                if (!data.workspaces.includes(context.id)) {
                    return;
                }
                context.flightLayerFacade.routes.upsertFlight(data.flightId, data);
            },
            [context]
        )
    );

    useSafeEffect(() => {
        context.flightLayerFacade.routes.flownRouteSettings = flownRouteSettings;
    }, [flownRouteSettings, context]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.flightPlanSettings = {
            ...flightPlanSettings,
            showTempPlanned: drawDirectRouteWhenNoRouteAvailable
        };
    }, [context, flightPlanSettings, drawDirectRouteWhenNoRouteAvailable]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.showProjectedPointsRouteFlown = showProjectedPoints;
    }, [showProjectedPoints, context]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.routeFlownColorSettings = {
            shouldColorize: shouldColorizeRouteFlown,
            lineColor: (routeFlowColor as ColorString) ?? DEFAULT_ROUTE_FLOWN_COLOR,
            // prettier-ignore
            flownPoints: {
                defaultColor:routeFlownSettings.defaultColor,
                projectedColor: routeFlownSettings.projected.enabled ? routeFlownSettings.projected.color : routeFlownSettings.defaultColor,
                acarsColor: routeFlownSettings.acars.enabled ? routeFlownSettings.acars.color : routeFlownSettings.defaultColor,
                adsbTerrestrialColor: routeFlownSettings.adsbTerrestrial.enabled ? routeFlownSettings.adsbTerrestrial.color : routeFlownSettings.defaultColor,
                adsbSatelliteColor: routeFlownSettings.adsbSatellite.enabled ? routeFlownSettings.adsbSatellite.color : routeFlownSettings.defaultColor,
                faaAnspColor: routeFlownSettings.faaAnsp.enabled ? routeFlownSettings.faaAnsp.color : routeFlownSettings.defaultColor
            }
        };
    }, [routeFlownSettings, shouldColorizeRouteFlown, routeFlowColor, context]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.shouldSmoothenRouteFlown = shouldSmoothenRouteFlown;
    }, [shouldSmoothenRouteFlown, context]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.etopsColorSettings = {
            borderFillColor: etopsBorderFillColor,
            borderOutlineColor: etopsBorderOutlineColor,
            ruleFillColor: etopsRuleFillColor,
            ruleOutlineColor: etopsRuleOutlineColor,
            pointColor: etopsPointColor,
            aptColor: etopsAirportColor
        };
    }, [
        etopsBorderFillColor,
        etopsBorderOutlineColor,
        etopsRuleFillColor,
        etopsRuleOutlineColor,
        etopsPointColor,
        etopsAirportColor,
        context
    ]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.showImpacts = showImpacts;
    }, [showImpacts, context]);

    useSafeEffect(() => {
        context.flightLayerFacade.routes.timeDistanceMode = timeDistanceMode as unknown as TimeDistanceMode;
    }, [timeDistanceMode, context]);

    return <></>;
}
